import { ref } from 'vue';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAdminStore } from '@/stores/admin';
import { useAuthStore } from '@/stores/auth';
import * as Sentry from "@sentry/vue";

/**
 * Get the minor version from a semver string (e.g., "1.2.6" -> "1.2")
 * Strips "dev-" prefix if present
 */
function getMinorVersion(version: string): string {
  // Remove "dev-" prefix if present
  const cleanVersion = version.replace(/^dev-/, '');
  const parts = cleanVersion.split('.');
  return `${parts[0]}.${parts[1]}`;
}

/**
 * Load changelog content for a specific minor version
 */
export async function getChangelogForVersion(version: string): Promise<string> {
  const minorVersion = getMinorVersion(version);
  
  try {
    // Dynamically import the changelog file
    const changelogModule = await import(`../changelogs/CHANGELOG-${minorVersion}.md?raw`);
    return changelogModule.default;
  } catch (error) {
    console.error(`Failed to load changelog for version ${minorVersion}:`, error);
    Sentry.captureException(error);
    return `# Changelog\n\nNo changelog available for version ${version}.`;
  }
}

/**
 * Compare two semver versions
 * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 * Strips "dev-" prefix if present
 */
function compareVersions(v1: string, v2: string): number {
  // Remove "dev-" prefix if present
  const cleanV1 = v1.replace(/^dev-/, '');
  const cleanV2 = v2.replace(/^dev-/, '');
  
  
  const parts1 = cleanV1.split('.').map(Number);
  const parts2 = cleanV2.split('.').map(Number);
  
  for (let i = 0; i < 3; i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;
    
    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }
  
  return 0;
}

/**
 * Check if user needs to see the changelog
 * Returns true if current version is newer than user's last seen version
 */
export function checkNeedsChangelog(userLastSeenVersion: string | undefined, currentVersion: string): boolean {
  
  if (!userLastSeenVersion) {
    return true; // First time user
  }
  
  const result = compareVersions(currentVersion, userLastSeenVersion) > 0;
  return result;
}

/**
 * Update user's last seen version in Firestore
 * Strips "dev-" prefix before storing
 */
export async function markChangelogSeen(userId: string, version: string): Promise<void> {
  try {
    // Remove "dev-" prefix before storing
    const cleanVersion = version.replace(/^dev-/, '');
    
    // Users are stored at the root 'users' collection, not under environments
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastSeenVersion: cleanVersion
    });
  } catch (error) {
    console.error('Failed to update user lastSeenVersion:', error);
    Sentry.captureException(error);
    throw error;
  }
}

/**
 * Composable for changelog functionality
 */
export function useChangelog() {
  const showChangelog = ref(false);
  const changelogContent = ref('');
  const isLoading = ref(false);
  
  async function loadChangelog(version: string) {
    isLoading.value = true;
    try {
      changelogContent.value = await getChangelogForVersion(version);
    } catch (error) {
      console.error('Failed to load changelog:', error);
      Sentry.captureException(error);
      changelogContent.value = '# Error\n\nFailed to load changelog.';
    } finally {
      isLoading.value = false;
    }
  }
  
  async function acknowledgeChangelog(userId: string, version: string) {
    try {
      await markChangelogSeen(userId, version);
      
      // Update local auth store state (store clean version without "dev-" prefix)
      const authStore = useAuthStore();
      if (authStore.fbUser) {
        authStore.fbUser.lastSeenVersion = version.replace(/^dev-/, '');
      }
      
      showChangelog.value = false;
    } catch (error) {
      console.error('Failed to mark changelog as seen:', error);
      Sentry.captureException(error);
      // Still close the modal even if update fails
      showChangelog.value = false;
    }
  }
  
  return {
    showChangelog,
    changelogContent,
    isLoading,
    loadChangelog,
    acknowledgeChangelog
  };
}
